class QuantitySelector extends HTMLElement {
  #available = true;
  #boundOnMinus;
  #boundOnPlus;
  #boundOnBlur;
  #boundOnFocus;

  #inputElement = null;
  #minusButtonElement = null;
  #plusButtonElement = null;

  static get observedAttributes() {
    return ['min', 'max', 'step'];
  }

  connectedCallback() {
    this.#inputElement = this.querySelector('[data-ref="quantityInput"]');
    this.#minusButtonElement = this.querySelector('[data-ref="minusButton"]');
    this.#plusButtonElement = this.querySelector('[data-ref="plusButton"]');

    if (!this.#inputElement || !this.#minusButtonElement || !this.#plusButtonElement) {
      return;
    }

    this.#available = !this.#inputElement.disabled;

    this.#boundOnMinus = this.#onDecrease.bind(this);
    this.#boundOnPlus = this.#onIncrease.bind(this);
    this.#boundOnBlur = this.#onBlur.bind(this);
    this.#boundOnFocus = this.#onFocus.bind(this);

    this.#minusButtonElement.addEventListener('click', this.#boundOnMinus);
    this.#plusButtonElement.addEventListener('click', this.#boundOnPlus);
    this.#inputElement.addEventListener('blur', this.#boundOnBlur);
    this.#inputElement.addEventListener('focus', this.#boundOnFocus);

    this.#updateButtonStates();
  }

  disconnectedCallback() {
    if (!this.#inputElement || !this.#minusButtonElement || !this.#plusButtonElement) return;

    this.#minusButtonElement.removeEventListener('click', this.#boundOnMinus);
    this.#plusButtonElement.removeEventListener('click', this.#boundOnPlus);
    this.#inputElement.removeEventListener('blur', this.#boundOnBlur);
    this.#inputElement.removeEventListener('focus', this.#boundOnFocus);

    this.#inputElement = null;
    this.#minusButtonElement = null;
    this.#plusButtonElement = null;
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue || !this.#inputElement) return;

    this.#inputElement.setAttribute(name, newValue);
    this.#applyConstraints();
  }

  get #min() {
    return parseInt(this.#inputElement.min) || 1;
  }

  get #max() {
    return parseInt(this.#inputElement.max);
  }

  get #step() {
    return parseInt(this.#inputElement.step) || 1;
  }

  get #tracksCartQuantity() {
    return this.#inputElement.hasAttribute('data-cart-quantity');
  }

  get #cartQuantity() {
    if (!this.#tracksCartQuantity) return 0;
    return parseInt(this.#inputElement.getAttribute('data-cart-quantity') || '0');
  }

  get #currentValue() {
    return parseInt(this.#inputElement.value) || 0;
  }

  get #effectiveMax() {
    if (!this.#tracksCartQuantity) return this.#max;
    return Math.max(this.#max - this.#cartQuantity, this.#min);
  }

  #canChange(quantity) {
    return quantity >= this.#min && quantity <= this.#effectiveMax;
  }

  canAddToCart() {
    const cartQuantity = this.#cartQuantity;
    const quantityToAdd = this.#currentValue;

    return {
      canAdd: cartQuantity + quantityToAdd <= this.#max,
      maxQuantity: this.#max,
      cartQuantity,
      quantityToAdd,
    };
  }

  setCartQuantity(cartQty) {
    this.#inputElement.setAttribute('data-cart-quantity', cartQty.toString());
    this.#clampToEffectiveMax();
    this.#updateButtonStates();
  }

  updateConstraints(min, max = '99', step) {
    this.setAttribute('min', min);
    this.setAttribute('max', max);
    this.setAttribute('step', step);
  }

  setAvailability(available) {
    this.#available = available;
    this.#inputElement.disabled = !available;
    this.#minusButtonElement.disabled = !available;
    this.#plusButtonElement.disabled = !available;
  }

  getValue() {
    return this.#inputElement.value;
  }

  setValue(value) {
    this.#inputElement.value = value;
  }

  #applyConstraints() {
    const newMin = this.#min;
    const newStep = this.#step;
    const current = this.#currentValue;

    let snapped = current;
    if ((current - newMin) % newStep !== 0) {
      snapped = newMin + Math.floor((current - newMin) / newStep) * newStep;
    }

    snapped = Math.max(newMin, Math.min(this.#effectiveMax, snapped));

    if (snapped !== current) this.#inputElement.value = snapped.toString();

    this.#updateButtonStates();
  }

  #onDecrease(event) {
    event.preventDefault();
    this.#stepQuantity(-1);
  }

  #onIncrease(event) {
    event.preventDefault();
    this.#stepQuantity(1);
  }

  #onFocus(_event) {
    this.#inputElement.select();
  }

  #onBlur(event) {
    if (!(event.target instanceof HTMLInputElement)) return;

    const clamped = Math.min(
      this.#effectiveMax,
      Math.max(this.#min, parseInt(event.target.value) || 0)
    );

    if (!this.#canChange(clamped)) return;

    if ((clamped - this.#min) % this.#step !== 0) {
      this.#inputElement.value = clamped.toString();
      this.#inputElement.reportValidity();
      return;
    }

    this.#inputElement.value = clamped.toString();
    this.#updateButtonStates();
  }

  #stepQuantity(direction) {
    const candidate = this.#currentValue + this.#step * direction;

    if (!this.#canChange(candidate)) return;

    this.#inputElement.value = candidate.toString();
    this.#updateButtonStates();
  }

  #clampToEffectiveMax() {
    const clamped = Math.min(this.#effectiveMax, Math.max(this.#min, this.#currentValue));
    if (clamped !== this.#currentValue) this.#inputElement.value = clamped.toString();
  }

  #updateButtonStates() {
    if (!this.#available) return;

    this.#minusButtonElement.disabled = this.#currentValue <= this.#min;
    this.#plusButtonElement.disabled = this.#currentValue >= this.#effectiveMax;
  }
}

if (!customElements.get('quantity-selector')) {
  customElements.define('quantity-selector', QuantitySelector);
}
